import React from 'react';
import { useComparison } from '../../context/ComparisonContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';

const ComparisonModal = ({ isOpen, onClose }) => {
  const { comparisonItems, removeFromComparison, clearComparison } = useComparison();
  const { addToCart, isInCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  if (!isOpen) return null;

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      );
    }
    
    const emptyStars = 5 - fullStars;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg key={`empty-${i}`} className="w-4 h-4 text-gray-300 fill-current" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      );
    }
    
    return stars;
  };

  const getComparisonFeatures = () => {
    if (comparisonItems.length === 0) return [];
    
    return [
      { label: 'Price', key: 'price', type: 'price' },
      { label: 'Rating', key: 'rating', type: 'rating' },
      { label: 'Category', key: 'category', type: 'text' },
      { label: 'Stock Status', key: 'inStock', type: 'boolean' },
      { label: 'Description', key: 'description', type: 'text' }
    ];
  };

  const getBestValue = (feature, items) => {
    if (feature.type === 'price') {
      return Math.min(...items.map(item => item[feature.key]));
    }
    if (feature.type === 'rating') {
      return Math.max(...items.map(item => item[feature.key]));
    }
    return null;
  };

  const features = getComparisonFeatures();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-4xl lg:max-w-6xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-3 sm:p-4 lg:p-6 rounded-t-xl sm:rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Product Comparison</h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">
                Compare {comparisonItems.length} product{comparisonItems.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {comparisonItems.length > 0 && (
                <button
                  onClick={clearComparison}
                  className="text-red-600 hover:text-red-700 font-medium text-xs sm:text-sm touch-optimized"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-optimized"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {comparisonItems.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products to Compare</h3>
            <p className="text-gray-600 mb-4">Add products to comparison to see them here</p>
            <button
              onClick={onClose}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="p-3 sm:p-4 lg:p-6">
            {/* Mobile View - Stack Cards */}
            <div className="block sm:hidden space-y-4">
              {comparisonItems.map(product => (
                <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm truncate">{product.name}</h3>
                      <p className="text-blue-600 font-bold text-lg">${product.price}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {renderStars(product.rating)}
                        <span className="text-xs text-gray-600 ml-1">({product.rating})</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromComparison(product.id)}
                      className="p-1 text-gray-400 hover:text-red-600 touch-optimized"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="space-y-2 text-sm mb-3">
                    <div><span className="font-medium">Category:</span> {product.category}</div>
                    <div><span className="font-medium">Stock:</span> 
                      <span className={`ml-1 px-2 py-1 rounded-full text-xs ${product.inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {product.inStock ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </div>
                    <div><span className="font-medium">Description:</span> {product.description.substring(0, 80)}...</div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => addToCart(product)}
                      className={`flex-1 py-2 px-3 text-sm rounded-lg font-medium transition-colors touch-optimized ${
                        isInCart(product.id)
                          ? 'bg-green-500 text-white'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isInCart(product.id) ? '✓ Added' : 'Add to Cart'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (isInWishlist(product.id)) {
                          removeFromWishlist(product.id);
                        } else {
                          addToWishlist(product);
                        }
                      }}
                      className={`p-2 border rounded-lg transition-colors touch-optimized ${
                        isInWishlist(product.id)
                          ? 'border-red-500 bg-red-50 text-red-500'
                          : 'border-gray-300 hover:border-red-300 hover:text-red-500'
                      }`}
                    >
                      <svg className="w-4 h-4" fill={isInWishlist(product.id) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop/Tablet View - Table Layout */}
            <div className="hidden sm:block">
              {/* Products Header */}
              <div className="overflow-x-auto">
                <div className="grid gap-4 mb-6 min-w-max" style={{ gridTemplateColumns: `150px repeat(${comparisonItems.length}, minmax(200px, 1fr))` }}>
                  <div></div>
                  {comparisonItems.map(product => (
                    <div key={product.id} className="text-center">
                      <div className="relative">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-24 sm:h-32 object-cover rounded-lg mb-3"
                        />
                        <button
                          onClick={() => removeFromComparison(product.id)}
                          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-red-50 hover:text-red-600 transition-colors touch-optimized"
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">{product.name}</h3>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-1 sm:gap-2">
                        <button
                          onClick={() => addToCart(product)}
                          className={`flex-1 py-2 px-2 sm:px-3 text-xs sm:text-sm rounded-lg font-medium transition-colors touch-optimized ${
                            isInCart(product.id)
                              ? 'bg-green-500 text-white'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isInCart(product.id) ? '✓ Added' : 'Add to Cart'}
                        </button>
                        
                        <button
                          onClick={() => {
                            if (isInWishlist(product.id)) {
                              removeFromWishlist(product.id);
                            } else {
                              addToWishlist(product);
                            }
                          }}
                          className={`p-2 border rounded-lg transition-colors touch-optimized ${
                            isInWishlist(product.id)
                              ? 'border-red-500 bg-red-50 text-red-500'
                              : 'border-gray-300 hover:border-red-300 hover:text-red-500'
                          }`}
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill={isInWishlist(product.id) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comparison Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
                <div className="min-w-max">
                  {features.map((feature, index) => {
                    const bestValue = getBestValue(feature, comparisonItems);
                    
                    return (
                      <div
                        key={feature.key}
                        className={`grid gap-4 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                        style={{ gridTemplateColumns: `150px repeat(${comparisonItems.length}, minmax(200px, 1fr))` }}
                      >
                        {/* Feature Label */}
                        <div className="p-3 sm:p-4 font-semibold text-gray-900 border-r border-gray-200 text-sm sm:text-base">
                          {feature.label}
                        </div>
                        
                        {/* Feature Values */}
                        {comparisonItems.map(product => {
                          const value = product[feature.key];
                          const isBest = bestValue !== null && value === bestValue;
                          
                          return (
                            <div
                              key={product.id}
                              className={`p-3 sm:p-4 border-r border-gray-200 last:border-r-0 ${
                                isBest ? 'bg-green-50 border-green-200' : ''
                              }`}
                            >
                              {feature.type === 'price' && (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                  <span className={`font-bold text-sm sm:text-base ${isBest ? 'text-green-600' : 'text-blue-600'}`}>
                                    ${value.toFixed(2)}
                                  </span>
                                  {isBest && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                      Best Price
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {feature.type === 'rating' && (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                  <div className="flex">
                                    {renderStars(value)}
                                  </div>
                                  <span className={`text-xs sm:text-sm ${isBest ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>
                                    {value}
                                  </span>
                                  {isBest && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                      Highest
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {feature.type === 'boolean' && (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  value
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {value ? 'In Stock' : 'Out of Stock'}
                                </span>
                              )}
                              
                              {feature.type === 'text' && (
                                <span className="text-gray-900 text-xs sm:text-sm">
                                  {feature.key === 'description' 
                                    ? `${value.substring(0, 60)}${value.length > 60 ? '...' : ''}`
                                    : value
                                  }
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonModal;
